import { Injectable, inject } from '@angular/core';
import {
  FuseNavigationItem,
  FuseNavigationService,
} from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { defaultNavigation } from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';
import { ShipmentService } from 'app/services/shipment.service';
import { from, of } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SearchMockApi {
  private _fuseMockApiService = inject(FuseMockApiService);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _shipmentService = inject(ShipmentService);

  private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.registerHandlers();
  }

  registerHandlers(): void {
    const flatNavigation = this._fuseNavigationService.getFlatNavigation(
      this._defaultNavigation
    );

    this._fuseMockApiService
      .onPost('api/common/search')
      .reply(({ request }) => {
        const query = cloneDeep(request.body.query.toLowerCase());
        console.log('query: ', query);

        return from(
          Promise.all([
            this._shipmentService.getTableQRInfo(0, 0, 1), // Quote requests
            this._shipmentService.getTableQRInfo(0, 0, 2), // Shipments
          ])
        ).pipe(
          mergeMap(([quoteRequests, shipments]) => {
            let quoteRequestsResults = quoteRequests;
            let shipmentsResults = shipments;
            let pagesResults = flatNavigation;

            // Only filter the results if a non-empty query is provided
            if (query !== '') {
              quoteRequestsResults = quoteRequests.filter(
                qr =>
                  qr.quoteRequestNumber?.toLowerCase().includes(query) ||
                  qr.bookingNumber?.toLowerCase().includes(query) ||
                  qr.commodity?.toLowerCase().includes(query) ||
                  qr.pol?.toLowerCase().includes(query) ||
                  qr.pod?.toLowerCase().includes(query) ||
                  qr.qrStatus?.toLowerCase().includes(query) ||
                  qr.status?.toLowerCase().includes(query) ||
                  qr.enquiryType?.toLowerCase().includes(query)
              );

              shipmentsResults = shipments.filter(
                shipment =>
                  shipment.quoteRequestNumber?.toLowerCase().includes(query) ||
                  shipment.bookingNumber?.toLowerCase().includes(query) ||
                  shipment.commodity?.toLowerCase().includes(query) ||
                  shipment.pol?.toLowerCase().includes(query) ||
                  shipment.pod?.toLowerCase().includes(query) ||
                  shipment.qrStatus?.toLowerCase().includes(query) ||
                  shipment.status?.toLowerCase().includes(query) ||
                  shipment.enquiryType?.toLowerCase().includes(query)
              );

              pagesResults = flatNavigation.filter(
                page =>
                  page.title?.toLowerCase().includes(query) ||
                  (page.subtitle && page.subtitle.includes(query))
              );
            }

            const results = [];

            // Handle Quote Requests
            if (quoteRequestsResults.length > 0) {
              results.push({
                id: 'quoteRequests',
                label: 'Quote Requests',
                results: quoteRequestsResults.map(result => ({
                  id: result.id,
                  quoteRequestNumber: result.quoteRequestNumber,
                  bookingNumber: result.bookingNumber,
                  enquiryType: result.enquiryType,
                  commodity: result.commodity,
                  pol: result.pol,
                  pod: result.pod,
                  qrStatus: result.qrStatus,
                  status: result.status,
                  link: '/quote-requests/view/' + result.quoteRequestNumber,
                })),
              });
            }

            // Handle Shipments
            if (shipmentsResults.length > 0) {
              results.push({
                id: 'shipments',
                label: 'Shipments',
                results: shipmentsResults.map(result => ({
                  id: result.id,
                  bookingNumber: result.bookingNumber,
                  quoteRequestNumber: result.quoteRequestNumber,
                  enquiryType: result.enquiryType,
                  commodity: result.commodity,
                  pol: result.pol,
                  pod: result.pod,
                  qrStatus: result.qrStatus,
                  status: result.status,
                  link: '/shipments/view/' + result.bookingNumber,
                })),
              });
            }

            // Handle Pages
            if (pagesResults.length > 0) {
              results.push({
                id: 'pages',
                label: 'Pages',
                results: pagesResults.map(result => ({
                  title: result.title,
                  link: result.link || '',
                })),
              });
            }

            console.log('results: ', results);
            return of([200, { results }]); // Return all results if the query is empty
          }),
          catchError(err => {
            console.error('Error during search:', err);
            return of([500, { error: 'Internal Server Error' }]); // Handle any errors
          })
        );
      });
  }
}
